import clsx from 'clsx'

import React, { useEffect, useRef, useState } from 'react'
import { getOptimizedUrl } from '@disco/api/imgixClient'
export type ImageProps = {
  url?: string
  params?: Record<string, any>
  useSrcSet?: boolean
  alt?: string
  cover?: boolean
  fetchPriority?: 'high' | 'low' | 'auto'
  loading?: 'lazy' | 'eager'
  decoding?: 'async' | 'sync' | 'auto'
}

/**
 * An optimized image component that uses Imgix for image optimization and supports responsive images.
 * The component automatically generates optimized image URLs based on container dimensions and parameters.
 *
 * @param url - The image URL to display
 * @param params - Optional parameters for image optimization (default: {})
 * @param useSrcSet - Whether to use srcSet for responsive images
 * @param alt - Alternative text for accessibility
 * @param cover - Whether to apply object-cover styling for proper aspect ratio handling
 * @returns The optimized image component, or null if no URL is provided
 */
const Image = ({
  url,
  params = {},
  useSrcSet,
  alt,
  cover,
  fetchPriority,
  loading,
  decoding,
}: ImageProps): null | React.ReactElement<'div'> => {
  const containerRef = useRef(null)
  const [buildURL, setBuildURL] = useState<string | null>(null)
  useEffect(() => {
    if (!url) {
      setBuildURL(null)
      return
    }

    const result = getOptimizedUrl({
      imageContainer: containerRef?.current,
      image: url,
      useSrcSet,
      params,
    })
    // Fallback to original URL if optimization fails
    setBuildURL(result || url)
  }, [containerRef?.current, url, useSrcSet, params])
  if (!url) return null
  return (
    <div className="relative w-full" ref={containerRef}>
      <img
        className={clsx('absolute inset-0 w-full h-full', {
          'object-cover': useSrcSet || cover,
        })}
        src={!useSrcSet ? buildURL ?? undefined : undefined}
        srcSet={useSrcSet ? buildURL ?? undefined : undefined}
        alt={alt}
        {...(decoding && { decoding })}
        {...(fetchPriority && { fetchPriority })}
        {...(loading && { loading })}
      />
    </div>
  )
}

export default Image
